import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useTableQuery } from '@/hooks/useTableQuery';
import { payslipsApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { ROUTES } from '@/app/router/routes';
import type { Payslip } from '@/types/hr';
import { formatDate } from '@/utils/format';
import { Days, Money } from './shared';

/**
 * An employee's own payslips.
 *
 * <p>Resolves the employee from the signed-in account — there is no identifier
 * in the request, so there is nothing to tamper with. Only approved runs appear:
 * before approval the figures are provisional, and showing somebody a draft
 * number they will later see change misleads them rather than informing them.
 */
export function MyPayslipsPage() {
  const navigate = useNavigate();
  const table = useTableQuery('createdAt,desc');

  const params = table.params;
  const payslips = useQuery({
    queryKey: queryKeys.payslips.mine(params),
    queryFn: () => payslipsApi.mine(params),
  });

  const columns: Column<Payslip>[] = [
    {
      key: 'period',
      header: 'Month',
      render: (row) => (
        <span>
          <span className="block text-sm font-medium text-ink">{row.periodName}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.slipNumber}</span>
        </span>
      ),
    },
    {
      key: 'paid',
      header: 'Paid',
      render: (row) => <span className="text-sm text-ink">{formatDate(row.paymentDate)}</span>,
    },
    {
      key: 'days',
      header: 'Days',
      className: 'text-right',
      render: (row) => (
        <span className="text-sm">
          <Days value={row.payableDays} />
          <span className="text-subtle">/{row.workingDays}</span>
        </span>
      ),
    },
    {
      key: 'gross',
      header: 'Gross',
      className: 'text-right',
      render: (row) => <Money value={row.grossEarnings} />,
    },
    {
      key: 'deductions',
      header: 'Deductions',
      className: 'text-right',
      render: (row) => <Money value={row.totalDeductions} />,
    },
    {
      key: 'net',
      header: 'Net pay',
      className: 'text-right',
      render: (row) => <Money value={row.netPay} bold />,
    },
  ];

  return (
    <div>
      <PageHeader
        title="My Payslips"
        description="Every month you have been paid. Open one to print it."
        icon={<FileText className="h-5 w-5" aria-hidden="true" />}
      />

      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          rows={payslips.data?.data}
          rowKey={(row) => row.id}
          loading={payslips.isPending}
          error={payslips.error}
          onRetry={() => void payslips.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(row) => navigate(ROUTES.payslip(row.id))}
          emptyTitle="No payslips yet"
          emptyDescription="A payslip appears here once the month's payroll has been approved."
        />
      </Card>
    </div>
  );
}
